/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Button, ButtonGroup, ButtonVariety, HelperText } from '@sonarsource/echoes-react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isDefined } from '~shared/helpers/types';
import {
  useGetAtlassianApplicationConfigurationQuery,
  usePostAtlassianApplicationConfigurationMutation,
} from '../../queries';
import { OrganizationJiraAppConfiguration } from './OrganizationJiraAppConfiguration';
import { OrganizationJiraForm } from './OrganizationJiraForm';
import { OrganizationJiraIntro } from './OrganizationJiraIntro';

interface OrganizationJiraNotConnectedProps {
  isCustomJiraApp: boolean;
  isFormDiscreet?: boolean;
  isPending: boolean;
  onConnect: () => void;
  sonarOrganizationName?: string;
}

export function OrganizationJiraNotConnected({
  isCustomJiraApp,
  isFormDiscreet,
  isPending,
  onConnect,
  sonarOrganizationName,
}: Readonly<OrganizationJiraNotConnectedProps>) {
  const [isFormDisplayed, setIsFormDisplayed] = useState(!isFormDiscreet);
  const [jiraAppCredentials, setJiraAppCredentials] = useState({
    jiraAppClientId: '',
    jiraAppSecret: '',
  });
  const [isAppConfigurationFormOpen, setIsAppConfigurationFormOpen] = useState(false);

  const { data: atlassianApplicationConfiguration } = useGetAtlassianApplicationConfigurationQuery({
    enabled: isCustomJiraApp,
  });
  const { mutate: postAtlassianApplicationConfiguration, isPending: isUpdatingAppConfiguration } =
    usePostAtlassianApplicationConfigurationMutation();

  const isAppConfigured = isDefined(atlassianApplicationConfiguration);
  const { jiraAppClientId, jiraAppSecret } = jiraAppCredentials;

  const onCancel = () => {
    setJiraAppCredentials({
      jiraAppClientId: '',
      jiraAppSecret: '',
    });
    setIsFormDisplayed(false);
  };
  const onShowForm = () => {
    setIsFormDisplayed(true);
  };
  const onUpdateCredentials = useCallback(
    (jiraAppCredentials: { jiraAppClientId: string; jiraAppSecret: string }) => {
      setJiraAppCredentials(jiraAppCredentials);
    },
    [],
  );

  const onUpdateAppConfiguration = () => {
    if (isCustomJiraApp && !isAppConfigured) {
      postAtlassianApplicationConfiguration(
        {
          clientId: jiraAppClientId,
          secret: jiraAppSecret,
        },
        {
          onSuccess: () => {
            onConnect();
          },
        },
      );
      return;
    }

    onConnect();
  };

  const isConnectButtonDisabled =
    isPending ||
    isAppConfigurationFormOpen ||
    (isCustomJiraApp && !isAppConfigured && jiraAppClientId === '' && jiraAppSecret === '');

  return (
    <>
      <div className="sw-mb-8">
        <OrganizationJiraIntro isConnected={false} sonarOrganizationName={sonarOrganizationName} />
      </div>
      {isFormDisplayed && (
        <>
          {isCustomJiraApp && (
            <div className="sw-mb-6">
              {isAppConfigured ? (
                <OrganizationJiraAppConfiguration
                  isPendingUpdate={isPending}
                  onChangeIsFormOpen={setIsAppConfigurationFormOpen}
                  onUpdatedAppConfiguration={onConnect}
                />
              ) : (
                <OrganizationJiraForm onUpdateCredentials={onUpdateCredentials} />
              )}
            </div>
          )}
          <ButtonGroup className="sw-flex sw-items-center sw-mt-6 sw-gap-2">
            <Button
              isDisabled={isConnectButtonDisabled}
              isLoading={isUpdatingAppConfiguration || isPending}
              name="connect"
              onClick={onUpdateAppConfiguration}
              type="submit"
              variety={ButtonVariety.Primary}
            >
              <FormattedMessage id="connect" />
            </Button>
            {isFormDiscreet && (
              <Button
                isDisabled={isUpdatingAppConfiguration || isPending}
                onClick={onCancel}
                variety={ButtonVariety.Default}
              >
                <FormattedMessage id="cancel" />
              </Button>
            )}
          </ButtonGroup>
        </>
      )}

      {!isFormDisplayed && (
        <div className="sw-flex sw-items-center sw-gap-2">
          <Button onClick={onShowForm} variety={ButtonVariety.Default}>
            <FormattedMessage id="settings.instance_integrations.start_setup" />
          </Button>
          <HelperText>
            <FormattedMessage id="organization.jira.binding.start_setup.hint" />
          </HelperText>
        </div>
      )}
    </>
  );
}
